// In-App form-API domain allowlist (IAM-094) — portal mirror of RRS service/inAppSettings.service.js
// (normalizeFormApiDomain / validateFormApiDomains; contract docs/in-app-sdk-payload.md
// "data.settings.formApiDomains"). The server re-validates; these give the same answer in the form.
import { parseCooldown, parseBrandColor, parseGlobalCap } from './inAppSettingsModel';

export const MAX_FORM_API_DOMAINS = 50;
const LABEL_RE = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/;

const isHostname = (h) => {
  const labels = h.split('.');
  return h.length <= 253 && labels.length >= 2 && labels.every((l) => l.length <= 63 && LABEL_RE.test(l)) && !/^\d+$/.test(labels[labels.length - 1]);
};

// Lower-case, one trailing dot dropped; hostname only (no scheme, path, port, userinfo, backslash,
// whitespace); `*.base` (base ≥ 2 labels) matches subdomains only. null = not allowed.
export function normalizeFormApiDomain(v) {
  if (typeof v !== 'string') return null;
  let d = v.trim().toLowerCase();
  if (d.endsWith('.')) d = d.slice(0, -1);
  if (!d || /[\s:/?#@[\]\\]/.test(d)) return null;
  if (d.startsWith('*.')) { const base = d.slice(2); return !base.includes('*') && isHostname(base) ? d : null; }
  return !d.includes('*') && isHostname(d) ? d : null;
}

const badEntry = (v) => `"${String(v).slice(0, 100)}" is not a hostname (no https://, path or port; wildcard only as a leading *. on a domain with two or more labels)`;

export function validateFormApiDomains(list) {
  if (!Array.isArray(list)) return { domains: null, error: 'form_api_domains must be a list of hostnames' };
  if (list.length > MAX_FORM_API_DOMAINS) return { domains: null, error: `At most ${MAX_FORM_API_DOMAINS} form API domains` };
  const out = [];
  for (const v of list) {
    const d = normalizeFormApiDomain(v);
    if (!d) return { domains: null, error: badEntry(v) };
    if (!out.includes(d)) out.push(d);
  }
  return { domains: out, error: null };
}

export function addFormApiDomain(current, input) {
  const list = current || [];
  const d = normalizeFormApiDomain(input);
  if (!d) return { domains: list, error: String(input || '').trim() ? badEntry(String(input).trim()) : 'Enter a hostname, e.g. api.example.com or *.example.com.' };
  if (list.includes(d)) return { domains: list, error: 'Already in the list.' };
  if (list.length >= MAX_FORM_API_DOMAINS) return { domains: list, error: `At most ${MAX_FORM_API_DOMAINS} form API domains.` };
  return { domains: [...list, d], error: null };
}

// One PUT carries both settings so saving one never wipes the other. `domainsKnown` = the GET
// returned form_api_domains; when an older backend didn't, the field is not sent at all.
// `extras` carries the later settings fields, each sent only when the GET returned it:
// { brandColor, brandKnown } (B-14), { brandDark, brandDarkKnown } (DA-BUG-1053), { schemes, schemesKnown } (P-8 deep_link_schemes).
export function buildSettingsPut(cooldownInput, domains, domainsKnown, extras = {}) {
  const errors = {};
  const c = parseCooldown(cooldownInput);
  if (c.error) errors.cooldown = c.error;
  let doms;
  if (domainsKnown) {
    const v = validateFormApiDomains(domains || []);
    if (v.error) errors.domains = v.error; else doms = v.domains;
  }
  let brand;
  if (extras.brandKnown) {
    const b = parseBrandColor(extras.brandColor);
    if (b.error) errors.brand = b.error; else brand = b.value;
  }
  let brandDark;
  if (extras.brandDarkKnown) {
    const b = parseBrandColor(extras.brandDark);
    if (b.error) errors.brandDark = b.error; else brandDark = b.value;
  }
  let capS; let capD;
  if (extras.capKnown) {
    const a = parseGlobalCap(extras.capSession); const b2 = parseGlobalCap(extras.capDay);
    if (a.error || b2.error) errors.cap = a.error || b2.error; else { capS = a.value; capD = b2.value; }
  }
  if (Object.keys(errors).length) return { body: null, errors };
  return { body: {
    global_cooldown_seconds: c.value,
    ...(domainsKnown ? { form_api_domains: doms } : {}),
    ...(extras.brandKnown ? { brand_color: brand } : {}),
    ...(extras.brandDarkKnown ? { brand_color_dark: brandDark } : {}),
    ...(extras.schemesKnown ? { deep_link_schemes: extras.schemes || [] } : {}),
    ...(extras.capKnown ? { global_max_per_session: capS, global_max_per_day: capD } : {}),
  }, errors };
}
